import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { formatCHF, parseDecimalInput, roundDisplayCHF } from '@werkdelta/shared';
import { Feld, Fehler } from '../../components/ui';
import { api, ApiError } from '../../api';
import type { StepProps } from '../CasePage';

const PARAM_KINDS = [
  'Q',
  'PRICE',
  'REPAIR_COST',
  'FULFILLMENT',
  'WEIGHT',
  'DISCOUNT_RATE',
  'YEARS_DEFECTIVE',
  'YEARS_SOUND',
] as const;

interface ParamRow {
  kind: string;
  nodeId: string;
  low: string;
  high: string;
}

/**
 * Schritt 9: Sensitivität (Tornado, Szenarien tief/mittel/hoch) und
 * Qualitätsprüfung. Die Bandbreite steht vor dem Punktwert (Auftrag 5.5).
 */
export function Step9Sensitivitaet({ caseId, data, readonly }: StepProps) {
  const calculations = useMemo(
    () =>
      data.valuationModels.flatMap((vm: any) =>
        vm.calculations.map((c: any) => ({ ...c, vmLabel: vm.referenceObject ?? vm.id.slice(0, 8) })),
      ),
    [data],
  );
  const [calcId, setCalcId] = useState<string>(calculations[0]?.id ?? '');
  const [calcDetail, setCalcDetail] = useState<any | null>(null);
  const [params, setParams] = useState<ParamRow[]>([
    { kind: 'Q', nodeId: '', low: '', high: '' },
  ]);
  const [scenario, setScenario] = useState({ lowQ: '', highQ: '' });
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rounding, setRounding] = useState<100 | 1000>(100);

  useEffect(() => {
    setCalcId(calculations[0]?.id ?? '');
  }, [calculations]);

  useEffect(() => {
    if (!calcId) {
      setCalcDetail(null);
      return;
    }
    api
      .get(`/api/v1/cases/${caseId}/calculations/${calcId}`)
      .then((c) => {
        setCalcDetail(c);
        if (c.sensitivityResult) setResult(c.sensitivityResult);
        else setResult(null);
      })
      .catch(() => setCalcDetail(null));
  }, [calcId, caseId]);

  const leaves: { id: string; label: string }[] = useMemo(() => {
    const tree = calcDetail?.inputSnapshot?.calcInput?.tree;
    if (!tree) return [];
    const out: { id: string; label: string }[] = [];
    const walk = (nodes: any[]) => {
      for (const n of nodes) {
        if (n.children?.length) walk(n.children);
        else out.push({ id: n.id, label: n.label ?? n.id });
      }
    };
    walk(tree.nodes);
    return out;
  }, [calcDetail]);

  async function run() {
    setError(null);
    setBusy(true);
    try {
      const preparedParams = params
        .filter((p) => p.low !== '' && p.high !== '')
        .map((p) => ({
          kind: p.kind,
          nodeId: p.nodeId || undefined,
          label: paramLabel(p),
          low: parseDecimalInput(p.low) ?? p.low,
          high: parseDecimalInput(p.high) ?? p.high,
        }));
      const scenarios =
        scenario.lowQ && scenario.highQ
          ? {
              low: [{ kind: 'Q', value: parseDecimalInput(scenario.lowQ) ?? scenario.lowQ }],
              mid: [],
              high: [{ kind: 'Q', value: parseDecimalInput(scenario.highQ) ?? scenario.highQ }],
            }
          : undefined;
      const res = await api.post(`/api/v1/cases/${caseId}/calculations/${calcId}/sensitivity`, {
        metric: 'REDUCTION_AMOUNT',
        params: preparedParams,
        scenarios,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  function paramLabel(p: ParamRow): string {
    const kindLabel = t(`sens.kind.${p.kind}`);
    if (p.kind === 'FULFILLMENT' || p.kind === 'WEIGHT') {
      const leaf = leaves.find((l) => l.id === p.nodeId);
      return `${kindLabel}: ${leaf?.label ?? p.nodeId}`;
    }
    return kindLabel;
  }

  if (calculations.length === 0) {
    return (
      <>
        <section className="karte">
          <h2>{t('step9.title')}</h2>
          <div className="leer-hinweis">{t('step9.noCalculation')}</div>
        </section>
        <QuellenKarte caseId={caseId} readonly={readonly} />
        <Checkliste />
      </>
    );
  }

  const tornado = result?.tornado;
  const band = result?.band;
  const maxSpan = tornado?.entries?.reduce((acc: number, e: any) => {
    const span = e.span ? Number(e.span) : 0;
    return span > acc ? span : acc;
  }, 0);

  return (
    <>
      <section className="karte">
        <h2>{t('step9.title')}</h2>
        <Feld label={t('step9.calcSelect')} required>
          <select value={calcId} onChange={(e) => setCalcId(e.target.value)}>
            {calculations.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.vmLabel} · {new Date(c.createdAt).toLocaleString('de-CH')} · {c.snapshotSha256.slice(0, 8)}
              </option>
            ))}
          </select>
        </Feld>

        <h3>{t('step9.paramsTitle')}</h3>
        <p className="erklaerung">{t('step9.paramsIntro')}</p>
        {params.map((p, idx) => (
          <div className="feld-zeile" key={idx}>
            <Feld label={t('step9.paramKind')}>
              <select
                value={p.kind}
                onChange={(e) => {
                  const next = [...params];
                  next[idx] = { ...p, kind: e.target.value, nodeId: '' };
                  setParams(next);
                }}
              >
                {PARAM_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`sens.kind.${k}`)}
                  </option>
                ))}
              </select>
            </Feld>
            {(p.kind === 'FULFILLMENT' || p.kind === 'WEIGHT') && (
              <Feld label={t('step9.paramNode')}>
                <select
                  value={p.nodeId}
                  onChange={(e) => {
                    const next = [...params];
                    next[idx] = { ...p, nodeId: e.target.value };
                    setParams(next);
                  }}
                >
                  <option value="">—</option>
                  {leaves.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Feld>
            )}
            <Feld label={t('step9.low')}>
              <input
                value={p.low}
                onChange={(e) => {
                  const next = [...params];
                  next[idx] = { ...p, low: e.target.value };
                  setParams(next);
                }}
              />
            </Feld>
            <Feld label={t('step9.high')}>
              <input
                value={p.high}
                onChange={(e) => {
                  const next = [...params];
                  next[idx] = { ...p, high: e.target.value };
                  setParams(next);
                }}
              />
            </Feld>
            <div style={{ alignSelf: 'center' }}>
              <button
                type="button"
                className="knopf-sekundaer knopf-klein"
                onClick={() => setParams(params.filter((_, i) => i !== idx))}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
        <div className="aktionen">
          <button
            type="button"
            className="knopf-sekundaer knopf-klein"
            onClick={() => setParams([...params, { kind: 'PRICE', nodeId: '', low: '', high: '' }])}
          >
            {t('step9.addParam')}
          </button>
        </div>

        <h3>{t('step9.scenariosTitle')}</h3>
        <p className="erklaerung">{t('step9.scenariosIntro')}</p>
        <div className="feld-zeile">
          <Feld label={t('step9.scenarioLowQ')} hint={t('step9.scenarioHint')}>
            <input
              value={scenario.lowQ}
              onChange={(e) => setScenario({ ...scenario, lowQ: e.target.value })}
            />
          </Feld>
          <Feld label={t('step9.scenarioHighQ')}>
            <input
              value={scenario.highQ}
              onChange={(e) => setScenario({ ...scenario, highQ: e.target.value })}
            />
          </Feld>
        </div>

        {!readonly && (
          <div className="aktionen">
            <button type="button" className="knopf-primaer" onClick={run} disabled={busy || !calcId}>
              {t('step9.run')}
            </button>
          </div>
        )}
        <Fehler code={error} />
      </section>

      {tornado && (
        <section className="karte">
          <h3>{t('step9.tornadoTitle')}</h3>
          {tornado.baseValue && (
            <p>
              {t('step9.baseValue')}: <strong className="betrag">{formatCHF(tornado.baseValue)}</strong>
            </p>
          )}
          {tornado.entries.map((entry: any, i: number) => (
            <div className="tornado-zeile" key={i}>
              <span>{entry.param.label ?? t(`sens.kind.${entry.param.kind}`)}</span>
              {entry.issues ? (
                <span className="warnung" style={{ margin: 0 }}>
                  {entry.issues.map((iss: any) => t(`calc.error.${iss.code}`, iss.params) + (iss.code.startsWith('BLOCKED') ? t(`calc.blocked.${iss.code}`) : '')).join(' · ')}
                </span>
              ) : (
                <div className="tornado-balken" role="img" aria-label={`${entry.param.label ?? entry.param.kind}: ${entry.lowValue} bis ${entry.highValue}`}>
                  <span
                    style={{
                      left: '0%',
                      width: `${maxSpan ? Math.max((Number(entry.span) / maxSpan) * 100, 2) : 2}%`,
                    }}
                  />
                </div>
              )}
              <span className="zahl">{entry.span ? formatCHF(entry.span) : '—'}</span>
            </div>
          ))}
        </section>
      )}

      {band?.band && (
        <section className="karte">
          <h3>{t('step9.bandTitle')}</h3>
          <Feld label={t('step9.displayRounding')}>
            <select value={rounding} onChange={(e) => setRounding(Number(e.target.value) as 100 | 1000)}>
              <option value={100}>{t('step9.round100')}</option>
              <option value={1000}>{t('step9.round1000')}</option>
            </select>
          </Feld>
          <p style={{ fontSize: '1.3rem' }}>
            <strong>
              CHF {roundDisplayCHF(band.band.min, rounding)} – CHF {roundDisplayCHF(band.band.max, rounding)}
            </strong>
          </p>
          <p className="meta">
            {t('step9.bandExact')}: {formatCHF(band.band.min)} – {formatCHF(band.band.max)} ·{' '}
            {t('step9.pointValue')}:{' '}
            {band.mid?.reduction ? formatCHF(band.mid.reduction.reductionAmount) : '—'}
          </p>
          <p className="hinweis">{t('step9.bandNote')}</p>
        </section>
      )}

      <QuellenKarte caseId={caseId} readonly={readonly} />
      <Checkliste />
    </>
  );
}

/**
 * Quellenregister (Auftrag 6): produktweite Regel-IDs (nur lesbar, mit
 * Prüfstatus) und Fallquellen der Organisation. Keine fingierten Quellen.
 */
function QuellenKarte({ caseId, readonly }: { caseId: string; readonly: boolean }) {
  const [sources, setSources] = useState<{ globalSources: any[]; caseSources: any[] } | null>(null);
  const [form, setForm] = useState({ ident: '', type: 'MARKTDATEN', citation: '', notes: '' });
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api
      .get(`/api/v1/cases/${caseId}/sources`)
      .then(setSources)
      .catch(() => setSources({ globalSources: [], caseSources: [] }));

  useEffect(() => {
    void load();
  }, [caseId]); // load haengt nur von caseId ab

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post(`/api/v1/cases/${caseId}/sources`, {
        ident: form.ident,
        type: form.type,
        citation: form.citation,
        notes: form.notes || undefined,
      });
      setForm({ ident: '', type: 'MARKTDATEN', citation: '', notes: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  async function remove(id: string) {
    await api.del(`/api/v1/cases/${caseId}/sources/${id}`).catch(() => undefined);
    await load();
  }

  const SOURCE_TYPES = ['RECHTSPRECHUNG', 'NORM', 'LITERATUR', 'MARKTDATEN', 'BEWERTUNG', 'SONSTIGE'];

  return (
    <section className="karte">
      <h3>{t('sources.title')}</h3>
      <p className="erklaerung">{t('sources.intro')}</p>
      {sources === null ? (
        <p>{t('app.loading')}</p>
      ) : (
        <div className="tabelle-scroll">
        <table className="daten">
          <thead>
            <tr>
              <th>{t('sources.ident')}</th>
              <th>{t('sources.type')}</th>
              <th>{t('sources.citation')}</th>
              <th>{t('sources.reviewStatus')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {sources.globalSources.map((src) => (
              <tr key={src.id}>
                <td>{src.ident}</td>
                <td>{t(`sources.type.${src.type}`)}</td>
                <td>{src.citation}</td>
                <td>
                  <span className={`badge ${src.reviewStatus === 'AUSSTEHEND' ? 'warn' : ''}`}>
                    {t(`report.sources.review.${src.reviewStatus}`)}
                  </span>
                </td>
                <td className="meta">{t('sources.global')}</td>
              </tr>
            ))}
            {sources.caseSources.map((src) => (
              <tr key={src.id}>
                <td>{src.ident}</td>
                <td>{t(`sources.type.${src.type}`)}</td>
                <td>{src.citation}</td>
                <td>
                  <span className="badge">{t(`report.sources.review.${src.reviewStatus}`)}</span>
                </td>
                <td>
                  {!readonly && (
                    <button
                      type="button"
                      className="knopf-sekundaer knopf-klein"
                      onClick={() => remove(src.id)}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sources.globalSources.length + sources.caseSources.length === 0 && (
              <tr>
                <td colSpan={5} className="meta">
                  {t('sources.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}
      {!readonly && (
        <form onSubmit={add} style={{ marginTop: '0.8rem' }}>
          <h4 style={{ margin: '0.4rem 0' }}>{t('sources.add')}</h4>
          <div className="feld-zeile">
            <Feld label={t('sources.ident')} required hint={t('sources.identHint')}>
              <input value={form.ident} onChange={(e) => setForm({ ...form, ident: e.target.value })} required />
            </Feld>
            <Feld label={t('sources.type')} required>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {SOURCE_TYPES.map((st) => (
                  <option key={st} value={st}>
                    {t(`sources.type.${st}`)}
                  </option>
                ))}
              </select>
            </Feld>
          </div>
          <Feld label={t('sources.citation')} required hint={t('sources.citationHint')}>
            <input value={form.citation} onChange={(e) => setForm({ ...form, citation: e.target.value })} required />
          </Feld>
          <Feld label={t('step2.notes')}>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Feld>
          <Fehler code={error} />
          <button type="submit" className="knopf-primaer knopf-klein">
            {t('common.add')}
          </button>
        </form>
      )}
    </section>
  );
}

/** Qualitätsprüfung (Auftrag 7.9) — Arbeitsliste für die Gutachterin. */
function Checkliste() {
  const items = ['sources', 'weights', 'doubleCount', 'reference', 'legalStatus', 'assumptions'];
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  return (
    <section className="karte">
      <h3>{t('step9.checklistTitle')}</h3>
      {items.map((item) => (
        <div className="kontrollzeile" key={item}>
          <input
            id={`check-${item}`}
            type="checkbox"
            checked={checked[item] ?? false}
            onChange={(e) => setChecked({ ...checked, [item]: e.target.checked })}
          />
          <label htmlFor={`check-${item}`}>{t(`step9.checklist.${item}`)}</label>
        </div>
      ))}
    </section>
  );
}
